// src/features/inventory/pages/BatchDetailPage/tabs/BatchInfo/index.jsx
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Divider, Link, Typography } from '@mui/material';
import {
  InfoOutlined as InfoIcon,
  Inventory as HistoryIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import LocalizedDateDistance from '../../../../../../i18n/LocalizedDateDistance';
import { useStockTransactions } from '../../../../hooks/useStockTransactions';
import { useInventoryStockPermissions } from '../../../../../auth/hooks/usePermissions';
import SectionHeader from '../../../../../../components/common/SectionHeader';
import InfoTip from '../../../../../../components/common/InfoTip';

function DetailField({ label, info, children }) {
  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <InfoTip title={info} size={14} />
      </Box>
      {children}
    </Box>
  );
}

export default function BatchInfo({ lot, lotId }) {
  const { t } = useTranslation(['inventory', 'common']);
  const [, setSearchParams] = useSearchParams();
  const { transactions, loading, fetchTransactionsByLot } = useStockTransactions();
  const { canRead: canReadStock } = useInventoryStockPermissions();

  useEffect(() => {
    if (canReadStock) fetchTransactionsByLot(lotId);
  }, [fetchTransactionsByLot, lotId, canReadStock]);

  const recentTransactions = transactions.slice(0, 5);

  const handleSeeFullLog = (e) => {
    e.preventDefault();
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.set('tab', 'stock_log');
      return next;
    });
  };

  return (
    <Box sx={{ p: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>

      {/* Left: batch metadata */}
      <Box>
        <SectionHeader icon={InfoIcon} title={t('inventory:batches.detail.info_section')} />
        <DetailField label={t('inventory:batches.detail.initial_quantity')} info={t('inventory:help.batches.initial_quantity')}>
          <Typography variant="body2">{lot.initial_quantity}</Typography>
        </DetailField>

        <Divider />

        <DetailField label={t('inventory:batches.detail.created_at')}>
          <Typography variant="body2">
            {lot.created_at ? new Date(lot.created_at).toLocaleDateString() : '—'}
          </Typography>
        </DetailField>

        <Divider />

        <DetailField label={t('inventory:batches.detail.notes')}>
          {lot.notes
            ? <Typography variant="body2">{lot.notes}</Typography>
            : <Typography variant="body2" color="text.secondary">{t('inventory:batches.detail.no_notes')}</Typography>
          }
        </DetailField>
      </Box>

      {/* Right: recent transactions */}
      {canReadStock && <Box>
        <SectionHeader icon={HistoryIcon} title={t('inventory:items.tabs.basic_info.quick_stock')} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : recentTransactions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('inventory:items.detail.no_stock_data')}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {recentTransactions.map((tx) => (
              <Box
                key={tx.stock_transaction_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.25,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tx.quantity_change >= 0
                    ? <TrendingUpIcon fontSize="small" color="success" />
                    : <TrendingDownIcon fontSize="small" color="error" />
                  }
                  <Typography
                    variant="body2"
                    color={tx.quantity_change >= 0 ? 'success.main' : 'error.main'}
                    fontWeight="medium"
                  >
                    {tx.quantity_change >= 0 ? '+' : ''}{tx.quantity_change}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`inventory:items.stock_reasons.${tx.reason}`)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  <LocalizedDateDistance date={tx.created_at} />
                </Typography>
              </Box>
            ))}
            <Link
              component="button"
              variant="body2"
              onClick={handleSeeFullLog}
              sx={{ alignSelf: 'flex-start', mt: 1.5 }}
            >
              {t('inventory:items.tabs.basic_info.see_full_log')} →
            </Link>
          </Box>
        )}
      </Box>}

    </Box>
  );
}
